import trimFormData from "../functions/trimFormData";
import { validateLoginForm, validateRegisterForm } from "../functions/Validators";
import localStore from "../storageAPIs/localStore";
import sessionStore from "../storageAPIs/sessionStore";
import TunnusSharedFunctions from "../TunnusSharedFunctions";
import clearUrl from "./clearUrl";
import InitCheck from "./InitCheck";
import LoginFunctions from "./LoginFunctions";
import { getLoginHandler, getLogoutHandler } from "./PopupHandlers";
import SessionFunctions from "./SessionFunctions";

const EmbeddedCoreUserInterface = (config, state, funcs, userStore, lng) => {
  const loginFunctions = LoginFunctions(funcs.authenticator, userStore);
  const sessionFunctions = SessionFunctions(config, state, funcs, loginFunctions);

  const removeSession = () => localStore.removeIdTokenData();

  const clearUser = () => userStore.clearUser();

  const redirectUniversal = (options = {}) => {
    sessionStore.setOriginUrl(window.location.href);
    sessionStore.setSource(options.source || state.source);
    funcs.authenticator.redirectToUniversal({ ...options, state, lng });
  };

  const isFacebookApp = (ua = navigator.userAgent || navigator.vendor || window.opera) =>
    (ua.indexOf("iPhone") > -1 || ua.indexOf("iPad") > -1) && (ua.indexOf("FBAN") > -1 || ua.indexOf("FBAV") > -1);

  const reset = () => {
    clearUser();
    removeSession();
  };

  const logout = (rTo) => {
    removeSession();
    const returnTo = rTo || config.returnTo;
    if (state.analyticsWait && funcs.analytics.isLoaded()) {
      return funcs.analytics.sendLogout(() => loginFunctions.logout(returnTo));
    }
    funcs.analytics.sendLogout();
    return loginFunctions.logout(returnTo);
  };

  return Object.assign({}, TunnusSharedFunctions(config, state, funcs), {
    runInitCheck: () => {
      if (config.runInitCheck !== false)
        InitCheck(config, state, funcs, loginFunctions)
          .run()
          .then((callback) => callback());
    },
    clearUrl,
    storageIsAvailable: state.storageIsAvailable,
    isRedirectToHostedPage: () => !state.cookiesEnabled || isFacebookApp(),
    getCookiesEnabled: () => state.cookiesEnabled,
    getEvents: () => funcs.events,
    reset,
    getCMPData: () => state.CMPData,
    login: async (username, password, suppressCallback = false) => {
      try {
        sessionFunctions.validate3rdPCEnabled();
        await validateLoginForm(username, password);
        const result = await loginFunctions.login(username, password);
        if (!suppressCallback) config.loginCallback(null, result);
        funcs.analytics.sendAuthenticationEvent(result.user, "login");
        return { success: result, errors: null };
      } catch (error) {
        funcs.analytics.sendLoginFailure("login");
        if (!suppressCallback) config.loginCallback(error, null);
        return { success: null, errors: Array.isArray(error) ? error : [error] };
      }
    },
    logout,
    registerAndLoginUser: async (formData) =>
      validateRegisterForm(trimFormData(formData))
        .then((data) => loginFunctions.registerAndLoginUser(data, state.type))
        .then((result) => {
          if (result.statusCode) funcs.analytics.sendRegistration(false);
          else {
            funcs.analytics.sendRegistration(true);
            funcs.analytics.sendAuthenticationEvent(result.user, "login");
            config.loginCallback(null, result);
          }
          return result;
        })
        .catch((error) => {
          const errors = Array.isArray(error) ? error : [error];
          if (errors.find((e) => e.error === "consent")) {
            funcs.analytics.sendRegistration(false, true);
            if (errors.length > 1) funcs.analytics.sendRegistration(false);
          } else funcs.analytics.sendRegistration(false);
          return { errors };
        }),

    redirectToHostedLogin: (options = {}) => redirectUniversal(options),

    redirectToHostedRegistration: (options = {}) => redirectUniversal({ ...options, modal: "REGISTER" }),

    setSessionFromSilentLogin: async () => {
      try {
        const result = await loginFunctions.silentNoPromptLogin(true, true);
        funcs.analytics.sendAuthenticationEvent(result.user, "login-silent");
        const originUrl = sessionStore.getOriginUrl();
        config.loginCallback(null, { ...result, originUrl });
        return result;
      } catch (error) {
        funcs.analytics.sendLoginFailure("login-silent");
        return funcs.logger.log(error);
      }
    },

    checkSession: (allowSilent, simpleReturn) => sessionFunctions.checkSession(allowSilent, simpleReturn),

    checkExternalSession: (isRefresh = false, simpleReturn = true) =>
      state.cookiesEnabled ? sessionFunctions.checkExternalSession(isRefresh, simpleReturn) : false,

    getAccessToken: () => sessionFunctions.getAccessToken(),

    getIdToken: localStore.getIdTokenData,

    idTokenIsValid: async () => {
      const idTokenData = localStore.getIdTokenData();
      return idTokenData
        ? funcs.authenticator
            .verifyIdToken(idTokenData)
            .then(() => true)
            .catch(() => false)
        : null;
    },
    browserIsSupported: () => typeof window !== "undefined" && window.safari === undefined,

    getUser: userStore.getUser,

    getUpdatedUser: async () => {
      const idTokenData = localStore.getIdTokenData();
      return idTokenData
        ? funcs.authenticator
            .verifyIdToken(idTokenData)
            .then((idToken) => {
              userStore.setUser(idToken, false);
              return userStore.getUser();
            })
            .catch(() => false)
        : false;
    },

    getIdJWT: async (options) => {
      const refresh = options?.refresh;
      const idTokenData = localStore.getIdTokenData();
      if (!idTokenData) return null;
      if (refresh && state.cookiesEnabled) {
        const refreshResult = await funcs.authenticator.silentLogin();
        funcs.loginFunctions.handleSilentResult(true, false)(refreshResult);
        return {
          idToken: refreshResult.idToken,
          idTokenPayload: refreshResult.idTokenPayload,
        };
      } else {
        const idTokenPayload = await funcs.authenticator.verifyIdToken(idTokenData);
        return { idToken: idTokenData.idToken, idTokenPayload };
      }
    },

    cookiesEnabled: () => state.cookiesEnabled,

    getLogoutHandler: getLogoutHandler(reset, funcs, logout),

    getLoginHandler: getLoginHandler(config, loginFunctions, funcs, state),
  });
};

export default EmbeddedCoreUserInterface;
